import { useQuery } from '@tanstack/react-query';
import { combineStatus, getSession } from '#shared/api/drills';
import type { DrillSessionDetail } from '#shared/schemas/drill';
import { api } from '@/lib/api';
import { offlineQuery } from '@/lib/query';

/**
 * Combine queries. Every key starts with 'combine', so one `invalidateQueries({ queryKey:
 * COMBINE_KEY })` refreshes the hub, the Home card and the results. The status is prefetched for
 * offline: the hub shows who has done what even on a court with no signal.
 */

export const COMBINE_KEY = ['combine'] as const;

export const combineKeys = {
  all: COMBINE_KEY,
  status: (ids?: readonly string[]) => ['combine', 'status', ids?.length ? [...ids].sort().join(',') : 'all'] as const,
  session: (id: string) => ['combine', 'session', id] as const,
};

export const fetchStatus = (ids?: readonly string[]) =>
  api.call(combineStatus, { query: ids?.length ? { playerIds: [...ids] } : {} });

export const statusQuery = (ids?: readonly string[]) => offlineQuery(combineKeys.status(ids), () => fetchStatus(ids));

/** Who has done which level. `ids` narrows it to some players; omitted means the whole crew. */
export function useCombineStatus(ids?: readonly string[]) {
  return useQuery({ ...statusQuery(ids), staleTime: 15_000 });
}

export function fetchDrillSession(id: string): Promise<DrillSessionDetail> {
  return api.call(getSession, { params: { id } });
}

/** One drill session with its shots, the games of an Iso Gauntlet and the rating deltas. */
export function useDrillSession(id: string, enabled = true) {
  return useQuery({
    queryKey: combineKeys.session(id),
    queryFn: () => fetchDrillSession(id),
    enabled,
    staleTime: 5_000,
    retry: false,
  });
}
